import React, { useEffect, useMemo, useState } from "react";
import {
    ShoppingBag,
    XCircle,
    User,
    UserX,
    Loader2,
    Phone,
    Mail,
    Bike,
    Users,
    Info,
    ShieldCheck,
    Clock3,
    CalendarDays,
    FileCheck,
    Smartphone,
    Package2,
    Package,
    ListOrdered,
    RefreshCcw,
    CheckCircle2,
    BadgeDollarSign,
    Truck,
    PackageCheck,
    BadgeInfo,
    Bell,
    ShieldAlert,
    MessageSquareText,
    ChevronRight,
    Filter
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useNotification from "../../Sevices/useNotification";
import { useGlobalContext } from "../../GlobalContext";

function NotificationHistory() {
    const {
        getNotifications,
        updateDriverOffDutyRequest,
        getDriverProfileDetails,
        getDriverAllOrders,
        updateOrderStatus,
        historyLoading,
        historyActionLoadingId,
        driverProfileLoading,
        orderDetailsLoading,
        orderStatusLoadingId,
    } = useNotification();

    const navigate = useNavigate();
    const { notificationRefreshKey, clearNotificationCount } = useGlobalContext();
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [refreshKey, setRefreshKey] = useState(0);

    const [actionModal, setActionModal] = useState({
        open: false,
        item: null,
        action: "",
        remarks: "",
    });
    const [orderDrawer, setOrderDrawer] = useState({
        open: false,
        item: null,
        orders: [],
        driverProfile: null,
        driverId: "",
        orderId: "",
        showMoreInfo: false,
    });
    const getNotificationType = (typeId) => {
        if (Number(typeId) === 1) return "driver_review";
        if (Number(typeId) === 2) return "off_duty";
        if (Number(typeId) === 3) return "order";
        return "general";
    };

    const loadNotifications = async () => {
        const data = await getNotifications([statusFilter]);

        const normalized = (data || []).map((item, index) => ({
            id: item?.pk_notification_id || index,
            notificationId: item?.pk_notification_id || null,
            typeId: Number(item?.pk_ntoification_type_id || 0),
            type: getNotificationType(item?.pk_ntoification_type_id),
            title: item?.title || "Notification",
            body: item?.body || "No message available",
            createdOn: item?.createdOn || null,
            driverCode: item?.driver_code || "",
            raw: item,
        }));

        setItems(normalized);
    };
    useEffect(() => {
        clearNotificationCount();
    }, []);
    useEffect(() => {
        loadNotifications();
    }, [statusFilter, refreshKey, notificationRefreshKey]);

    const groupedItems = useMemo(() => {
        return items;
    }, [items]);

    const formatDateTime = (value) => {
        if (!value) return "—";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return new Intl.DateTimeFormat("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    };

    const extractDriverId = (text = "") => {
        const match = text.match(/\bPD\d+\b/i);
        return match ? match[0] : "";
    };
    const extractOrderId = (text = "") => {
        const match = text.match(/\border\s*(\d+)\b/i);
        return match ? match[1] : "";
    };

    const openActionModal = (item, action) => {
        setActionModal({
            open: true,
            item,
            action,
            remarks: "",
        });
    };

    const closeActionModal = () => {
        setActionModal({
            open: false,
            item: null,
            action: "",
            remarks: "",
        });
    };
    const openDriverReview = (item) => {
        navigate("/driver", {
            state: {
                driverCode: item?.driverCode || extractDriverId(item?.body),
                fromNotification: true,
                notificationId: item?.notificationId,
            },
        });
    };

    const openOrderDrawer = async (item) => {
        const driverId = item?.driverCode || extractDriverId(item?.body);
        const orderId = extractOrderId(item?.body);

        setOrderDrawer({
            open: true,
            item,
            orders: [],
            driverProfile: null,
            driverId,
            orderId,
            showMoreInfo: false,
        });

        const [profile, orderData] = await Promise.all([
            getDriverProfileDetails(driverId),
            getDriverAllOrders({ driverId, orderId }),
        ]);

        const flattenedOrders = (orderData || []).flatMap(
            (group) => group?.orderDetails || [],
        );

        setOrderDrawer((prev) => ({
            ...prev,
            driverProfile: profile,
            orders: flattenedOrders,
        }));
    };

    const closeOrderDrawer = () => {
        setOrderDrawer({
            open: false,
            item: null,
            orders: [],
            driverId: "",
            orderId: "",
        });
    };

    const handleNotificationClick = async (item) => {
        if (item.type === "driver_review") {
            openDriverReview(item);
            return;
        }

        if (item.type === "order") {
            await openOrderDrawer(item);
        }
    };
    const handleSubmitAction = async () => {
        const currentItem = actionModal.item;
        if (!currentItem?.notificationId) return;

        await updateDriverOffDutyRequest({
            status: actionModal.action,
            driverId: extractDriverId(currentItem?.body),
            notificationId: currentItem.notificationId,
            adminRemarks: actionModal.remarks,
            onSuccess: async () => {
                closeActionModal();
                await loadNotifications();
            },
        });
    };
    const handleOrderStatusUpdate = async (orderId, status) => {
        await updateOrderStatus({
            orderId,
            status,
            onSuccess: async () => {
                const data = await getDriverAllOrders({
                    driverId: orderDrawer.driverId,
                    orderId: orderDrawer.orderId,
                });

                const flattenedOrders = (data || []).flatMap((group) => group?.orderDetails || []);

                setOrderDrawer((prev) => ({
                    ...prev,
                    orders: flattenedOrders,
                }));

                await loadNotifications();
            },
        });
    };
    const toggleDriverMoreInfo = () => {
        setOrderDrawer((prev) => ({
            ...prev,
            showMoreInfo: !prev.showMoreInfo,
        }));
    };
    const renderTypeIcon = (type) => {
        if (type === "off_duty") {
            return (
                <div className="w-11 h-11 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Bell className="w-5 h-5 text-orange-600" />
                </div>
            );
        }

        if (type === "driver_review") {
            return (
                <div className="w-11 h-11 rounded-2xl bg-amber-100 flex items-center justify-center">
                    <ShieldAlert className="w-5 h-5 text-amber-600" />
                </div>
            );
        }

        if (type === "order") {
            return (
                <div className="w-11 h-11 rounded-2xl bg-blue-100 flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-blue-600" />
                </div>
            );
        }

        return (
            <div className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center">
                <MessageSquareText className="w-5 h-5 text-gray-600" />
            </div>
        );
    };

    return (
        <div className="w-full text-left">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-xl font-bold text-gray-800">
                        Notification History
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        View all admin notifications and take action on off-duty requests.
                    </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-2xl border border-gray-200 bg-white">
                        <Filter className="w-4 h-4 text-gray-500" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="bg-transparent outline-none text-sm text-gray-700"
                        >
                            <option value="all">All</option>
                            <option value="seen">Seen</option>
                            <option value="unseen">Unseen</option>
                        </select>
                    </div>

                    <button
                        onClick={() => setRefreshKey((p) => p + 1)}
                        className="flex items-center gap-2 px-4 py-2 rounded-2xl border border-gray-200 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 transition-all"
                    >
                        <RefreshCcw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </div>

            {historyLoading ? (
                <div className="py-16 flex flex-col items-center justify-center text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-3" />
                    <p className="text-sm text-gray-500">Loading notification history...</p>
                </div>
            ) : groupedItems.length === 0 ? (
                <div className="py-16 text-center border border-dashed border-gray-200 rounded-3xl">
                    <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h3 className="text-lg font-semibold text-gray-700">
                        No notifications found
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        There is no notification history for the selected filter.
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {groupedItems.map((item) => {

                        const isOffDuty = item.type === "off_duty";
                        const isClickable = item.type === "driver_review" || item.type === "order";

                        return (
                            <div
                                key={`${item.type}-${item.id}`}
                                onClick={() => isClickable && handleNotificationClick(item)}
                                className={`rounded-3xl border border-gray-200 bg-white p-5 shadow-sm transition-all ${isClickable ? "cursor-pointer hover:shadow-md hover:border-orange-200" : "hover:shadow-md"
                                    }`}
                            >
                                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                                    <div className="flex gap-4 min-w-0 flex-1">
                                        {renderTypeIcon(item.type)}

                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                                <h3 className="text-base font-semibold text-gray-800">
                                                    {item.title}
                                                </h3>

                                                <span
                                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${item.type === "off_duty"
                                                        ? "bg-orange-100 text-orange-700"
                                                        : item.type === "driver_review"
                                                            ? "bg-amber-100 text-amber-700"
                                                            : item.type === "order"
                                                                ? "bg-blue-100 text-blue-700"
                                                                : "bg-gray-100 text-gray-700"
                                                        }`}
                                                >
                                                    {item.type === "off_duty"
                                                        ? "Off Duty Request"
                                                        : item.type === "driver_review"
                                                            ? "Driver Review"
                                                            : item.type === "order"
                                                                ? "Order Notification"
                                                                : "Notification"}
                                                </span>
                                            </div>

                                            <p className="text-sm leading-6 text-gray-600 whitespace-pre-line">
                                                {item.body}
                                            </p>

                                            <div className="flex items-center gap-2 mt-3 text-xs text-gray-500">
                                                <Clock3 className="w-4 h-4" />
                                                <span>{formatDateTime(item.createdOn)}</span>
                                            </div>
                                            {isClickable && (
                                                <div className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-orange-600">
                                                    <span>{item.type === "driver_review" ? "Open Driver Page" : "View Order Details"}</span>
                                                    <ChevronRight className="w-4 h-4" />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {isOffDuty && (
                                        <div className="flex flex-wrap gap-2 xl:pl-4">
                                            <button
                                                onClick={() => openActionModal(item, "yes")}
                                                disabled={historyActionLoadingId === item.notificationId}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium transition-all disabled:opacity-60"
                                            >
                                                {historyActionLoadingId === item.notificationId ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <CheckCircle2 className="w-4 h-4" />
                                                )}
                                                Approve
                                            </button>

                                            <button
                                                onClick={() => openActionModal(item, "no")}
                                                disabled={historyActionLoadingId === item.notificationId}
                                                className="inline-flex items-center gap-2 px-4 py-2 rounded-2xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-all disabled:opacity-60"
                                            >
                                                {historyActionLoadingId === item.notificationId ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <XCircle className="w-4 h-4" />
                                                )}
                                                Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {orderDrawer.open && (
                <div className="fixed inset-0 z-[70] flex justify-end bg-black/45 backdrop-blur-[2px]">
                    <div className="flex h-full w-full max-w-[920px] flex-col border-l border-orange-100 bg-gradient-to-b from-white to-white shadow-2xl">
                        {/* Header */}
                        <div className="sticky top-0 z-20 border-b border-orange-100 bg-white/95 backdrop-blur">
                            <div className="flex items-start justify-between gap-4 px-6 py-5">
                                <div className="min-w-0">
                                    <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                                        <ShoppingBag className="h-3.5 w-3.5" />
                                        Order Notification
                                    </div>

                                    <h3 className="truncate text-xl font-bold tracking-tight text-gray-900">
                                        Driver Order Details
                                    </h3>

                                    {/* <p className="mt-1 text-sm text-gray-500">
                                        Review driver profile, ordered items, and update the order status.
                                    </p> */}
                                </div>

                                <button
                                    onClick={closeOrderDrawer}
                                    className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700"
                                >
                                    <XCircle className="h-4 w-4" />
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            <div className="space-y-5">
                                {/* Driver Summary */}
                                <div className="overflow-hidden rounded-[28px] border border-orange-100 bg-white shadow-sm">
                                    <div className="border-b border-orange-100 bg-gradient-to-r from-orange-50 via-amber-50 to-white px-5 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-100">
                                                <User className="h-5 w-5 text-orange-600" />
                                            </div>
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-900">
                                                    Driver Summary
                                                </h4>
                                                <p className="text-xs text-gray-500">
                                                    Driver who placed the order
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-5">
                                        {driverProfileLoading ? (
                                            <div className="flex items-center justify-center gap-3 rounded-3xl border border-dashed border-orange-200 bg-orange-50/50 px-4 py-10 text-sm text-gray-600">
                                                <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
                                                Loading driver details...
                                            </div>
                                        ) : orderDrawer.driverProfile ? (
                                            <>
                                                <div className="flex flex-col gap-4 md:flex-row md:items-start">
                                                    <div className="relative">
                                                        <img
                                                            src={
                                                                orderDrawer.driverProfile?.profileUrl ||
                                                                "/placeholder-user.png"
                                                            }
                                                            alt={orderDrawer.driverProfile?.username || "Driver"}
                                                            className="h-20 w-20 rounded-[22px] border border-orange-200 bg-orange-50 object-cover shadow-sm"
                                                        />
                                                        <div className="absolute -bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white bg-white px-2 py-1 text-[10px] font-semibold text-gray-700 shadow">
                                                            <div
                                                                className={`h-2 w-2 rounded-full ${String(
                                                                    orderDrawer.driverProfile?.duty_status || "",
                                                                ).toLowerCase().includes("on")
                                                                    ? "bg-emerald-500"
                                                                    : "bg-red-500"
                                                                    }`}
                                                            />
                                                            {orderDrawer.driverProfile?.duty_status || "—"}
                                                        </div>
                                                    </div>

                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <h3 className="text-lg font-bold text-gray-900">
                                                                {orderDrawer.driverProfile?.username || "Driver"}
                                                            </h3>

                                                            <span className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700">
                                                                {orderDrawer.driverProfile?.driverCode || "—"}
                                                            </span>

                                                            {!!orderDrawer.driverProfile?.kycStatus && (
                                                                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold capitalize text-emerald-700">
                                                                    {orderDrawer.driverProfile?.kycStatus}
                                                                </span>
                                                            )}
                                                        </div>

                                                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                                                            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                                                                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                    <Phone className="h-3.5 w-3.5" />
                                                                    Phone
                                                                </div>
                                                                <p className="truncate text-sm font-semibold text-gray-800">
                                                                    {orderDrawer.driverProfile?.phone || "—"}
                                                                </p>
                                                            </div>

                                                            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                                                                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                    <Mail className="h-3.5 w-3.5" />
                                                                    Email
                                                                </div>
                                                                <p className="truncate text-sm font-semibold text-gray-800">
                                                                    {orderDrawer.driverProfile?.email || "—"}
                                                                </p>
                                                            </div>

                                                            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                                                                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                    <Bike className="h-3.5 w-3.5" />
                                                                    Vehicle
                                                                </div>
                                                                <p className="truncate text-sm font-semibold text-gray-800">
                                                                    {orderDrawer.driverProfile?.transportType || "—"}
                                                                </p>
                                                            </div>

                                                            <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3">
                                                                <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                    <Users className="h-3.5 w-3.5" />
                                                                    Active Team
                                                                </div>
                                                                <p className="truncate text-sm font-semibold text-gray-800">
                                                                    {orderDrawer.driverProfile?.activeTeamName || "—"}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={toggleDriverMoreInfo}
                                                        className="inline-flex shrink-0 items-center gap-2 self-start rounded-2xl border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 transition-all hover:bg-orange-100"
                                                    >
                                                        <Info className="h-4 w-4" />
                                                        {orderDrawer.showMoreInfo ? "Hide Info" : "More Info"}
                                                    </button>
                                                </div>

                                                {orderDrawer.showMoreInfo && (
                                                    <div className="mt-5 grid grid-cols-1 gap-3 border-t border-orange-100 pt-5 sm:grid-cols-2 xl:grid-cols-3">
                                                        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                                                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                <ShieldCheck className="h-4 w-4" />
                                                                Primary Team
                                                            </div>
                                                            <p className="text-sm font-semibold text-gray-800">
                                                                {orderDrawer.driverProfile?.primaryTeamName || "—"}
                                                            </p>
                                                        </div>

                                                        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                                                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                <Clock3 className="h-4 w-4" />
                                                                Shift End
                                                            </div>
                                                            <p className="text-sm font-semibold text-gray-800">
                                                                {orderDrawer.driverProfile?.shift_end || "—"}
                                                            </p>
                                                        </div>

                                                        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                                                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                <BadgeInfo className="h-4 w-4" />
                                                                Licence Plate
                                                            </div>
                                                            <p className="text-sm font-semibold text-gray-800">
                                                                {orderDrawer.driverProfile?.licencePlate || "—"}
                                                            </p>
                                                        </div>

                                                        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                                                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                <CalendarDays className="h-4 w-4" />
                                                                Joining Date
                                                            </div>
                                                            <p className="text-sm font-semibold text-gray-800">
                                                                {orderDrawer.driverProfile?.joiningDate
                                                                    ? formatDateTime(orderDrawer.driverProfile.joiningDate)
                                                                    : "—"}
                                                            </p>
                                                        </div>

                                                        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                                                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                <FileCheck className="h-4 w-4" />
                                                                KYC Step
                                                            </div>
                                                            <p className="text-sm font-semibold text-gray-800">
                                                                {orderDrawer.driverProfile?.kycStep || "—"}
                                                            </p>
                                                        </div>

                                                        <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
                                                            <div className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                                <Smartphone className="h-4 w-4" />
                                                                Device ID
                                                            </div>
                                                            <p className="truncate text-sm font-semibold text-gray-800">
                                                                {orderDrawer.driverProfile?.deviceId || "—"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-4 py-10 text-center">
                                                <UserX className="mx-auto mb-3 h-10 w-10 text-gray-300" />
                                                <p className="text-sm font-medium text-gray-600">
                                                    Driver details not found
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Orders Section */}
                                <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm">
                                    <div className="border-b border-gray-100 px-5 py-4">
                                        <div className="flex flex-wrap items-center justify-between gap-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-blue-100">
                                                    <Package2 className="h-5 w-5 text-blue-600" />
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-semibold text-gray-900">
                                                        Order Records
                                                    </h4>
                                                    <p className="text-xs text-gray-500">
                                                        {orderDrawer.orderId
                                                            ? `Showing details for order #${orderDrawer.orderId}`
                                                            : "Showing all available driver orders"}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-600">
                                                {orderDrawer.orders?.length || 0} Order
                                                {(orderDrawer.orders?.length || 0) > 1 ? "s" : ""}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-5">
                                        {orderDetailsLoading ? (
                                            <div className="flex items-center justify-center gap-3 rounded-3xl border border-dashed border-orange-200 bg-orange-50/40 px-4 py-14 text-sm text-gray-600">
                                                <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
                                                Loading order details...
                                            </div>
                                        ) : orderDrawer.orders?.length === 0 ? (
                                            <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-4 py-14 text-center">
                                                <ShoppingBag className="mx-auto mb-3 h-12 w-12 text-gray-300" />
                                                <h4 className="text-base font-semibold text-gray-800">
                                                    No orders found
                                                </h4>
                                                <p className="mt-1 text-sm text-gray-500">
                                                    There are no order details available for this driver.
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-5">
                                                {orderDrawer.orders.map((order) => (
                                                    <div
                                                        key={order?.pk_order_id}
                                                        className="overflow-hidden rounded-[26px] border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-sm"
                                                    >
                                                        {/* Order Head */}
                                                        <div className="border-b border-gray-100 bg-white px-5 py-4">
                                                            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                                                                <div className="min-w-0">
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <h4 className="text-lg font-bold text-gray-900">
                                                                            Order #{order?.pk_order_id || "—"}
                                                                        </h4>

                                                                        {order?.payment_mode && (
                                                                            <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-xs font-semibold text-violet-700">
                                                                                {order.payment_mode}
                                                                            </span>
                                                                        )}

                                                                        {order?.status && (
                                                                            <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold capitalize text-blue-700">
                                                                                {order.status}
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    <p className="mt-1 text-sm text-gray-500">
                                                                        Created{" "}
                                                                        {order?.created_on
                                                                            ? formatDateTime(order.created_on)
                                                                            : "—"}
                                                                    </p>
                                                                </div>

                                                                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                                                    <div className="rounded-2xl border border-gray-100 bg-orange-50 p-3 text-center">
                                                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-orange-700">
                                                                            Amount
                                                                        </p>
                                                                        <p className="mt-1 text-sm font-bold text-gray-900">
                                                                            ₹ {Number(order?.price || 0).toFixed(2)}
                                                                        </p>
                                                                    </div>

                                                                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 text-center">
                                                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Items
                                                                        </p>
                                                                        <p className="mt-1 text-sm font-bold text-gray-900">
                                                                            {order?.order_details?.length || 0}
                                                                        </p>
                                                                    </div>

                                                                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 text-center">
                                                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Payment
                                                                        </p>
                                                                        <p className="mt-1 text-sm font-bold text-gray-900">
                                                                            {order?.payment_mode || "—"}
                                                                        </p>
                                                                    </div>

                                                                    <div className="rounded-2xl border border-gray-100 bg-gray-50 p-3 text-center">
                                                                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                                                            Status
                                                                        </p>
                                                                        <p className="mt-1 text-sm font-bold capitalize text-gray-900">
                                                                            {order?.status || "—"}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Order Items */}
                                                        <div className="px-5 py-5">
                                                            <div className="mb-3 flex items-center gap-2">
                                                                <ListOrdered className="h-4 w-4 text-gray-500" />
                                                                <p className="text-sm font-semibold text-gray-800">
                                                                    Ordered Items
                                                                </p>
                                                            </div>

                                                            <div className="space-y-3">
                                                                {(order?.order_details || []).map((product, idx) => (
                                                                    <div
                                                                        key={`${order?.pk_order_id}-${product?.itemId}-${idx}`}
                                                                        className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-3 shadow-sm"
                                                                    >
                                                                        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-gray-50">
                                                                            <img
                                                                                src={product?.itemImage}
                                                                                alt={product?.itemName}
                                                                                className="h-full w-full object-cover"
                                                                            />
                                                                        </div>

                                                                        <div className="min-w-0 flex-1">
                                                                            <p className="truncate text-sm font-semibold text-gray-900">
                                                                                {product?.itemName || "Item"}
                                                                            </p>

                                                                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                                                <span className="rounded-full bg-gray-100 px-2 py-1 font-medium">
                                                                                    Item ID: {product?.itemId || "—"}
                                                                                </span>
                                                                                <span className="rounded-full bg-orange-100 px-2 py-1 font-medium text-orange-700">
                                                                                    Qty: {product?.quantity || "0"}
                                                                                </span>
                                                                            </div>
                                                                        </div>

                                                                        <div className="hidden sm:flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50">
                                                                            <Package className="h-5 w-5 text-orange-600" />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Bottom Actions */}
                                                            <div className="mt-5 border-t border-gray-100 pt-5">
                                                                <div className="mb-3 flex items-center gap-2">
                                                                    <RefreshCcw className="h-4 w-4 text-orange-600" />
                                                                    <p className="text-sm font-semibold text-gray-800">
                                                                        Update Order Status
                                                                    </p>
                                                                </div>

                                                                <div className="flex flex-wrap gap-2.5">
                                                                    {[
                                                                        {
                                                                            label: "Accepted",
                                                                            icon: CheckCircle2,
                                                                            cls: "bg-emerald-600 text-white hover:bg-emerald-700",
                                                                        },
                                                                        {
                                                                            label: "Rejected",
                                                                            icon: XCircle,
                                                                            cls: "bg-red-600 text-white hover:bg-red-700",
                                                                        },
                                                                        {
                                                                            label: "Paid",
                                                                            icon: BadgeDollarSign,
                                                                            cls: "bg-violet-600 text-white hover:bg-violet-700",
                                                                        },
                                                                        {
                                                                            label: "On The Way",
                                                                            icon: Truck,
                                                                            cls: "bg-orange-500 text-white hover:bg-orange-600",
                                                                        },
                                                                        {
                                                                            label: "Delivered",
                                                                            icon: PackageCheck,
                                                                            cls: "bg-blue-600 text-white hover:bg-blue-700",
                                                                        },
                                                                    ].map((action) => {
                                                                        const Icon = action.icon;
                                                                        const isUpdating =
                                                                            orderStatusLoadingId === order?.pk_order_id;

                                                                        return (
                                                                            <button
                                                                                key={action.label}
                                                                                onClick={() =>
                                                                                    handleOrderStatusUpdate(
                                                                                        order?.pk_order_id,
                                                                                        action.label,
                                                                                    )
                                                                                }
                                                                                disabled={isUpdating}
                                                                                className={`inline-flex items-center gap-2 rounded border px-4 py-2.5 text-sm font-semibold shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-60 ${action.cls}`}
                                                                            >
                                                                                {isUpdating ? (
                                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                                ) : (
                                                                                    <Icon className="h-4 w-4" />
                                                                                )}
                                                                                {action.label}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {actionModal.open && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="w-full max-w-lg rounded-3xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
                        <div className="px-6 py-5 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">
                                {actionModal.action === "yes"
                                    ? "Approve Off Duty Request"
                                    : "Reject Off Duty Request"}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                Add remarks for this action.
                            </p>
                        </div>

                        <div className="p-6">
                            <div className="mb-4 p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                <p className="text-sm font-medium text-gray-800 mb-1">
                                    {actionModal.item?.title}
                                </p>
                                <p className="text-sm text-gray-600 whitespace-pre-line">
                                    {actionModal.item?.body}
                                </p>
                            </div>

                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Admin Remarks
                            </label>
                            <textarea
                                rows={4}
                                value={actionModal.remarks}
                                onChange={(e) =>
                                    setActionModal((prev) => ({
                                        ...prev,
                                        remarks: e.target.value,
                                    }))
                                }
                                placeholder="Write remarks here..."
                                className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 resize-none focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400"
                            />
                        </div>

                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                onClick={closeActionModal}
                                className="px-4 py-2 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition-all"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleSubmitAction}
                                disabled={
                                    historyActionLoadingId === actionModal.item?.notificationId
                                }
                                className={`px-4 py-2 rounded-2xl text-white font-medium transition-all ${actionModal.action === "yes"
                                    ? "bg-emerald-500 hover:bg-emerald-600"
                                    : "bg-red-500 hover:bg-red-600"
                                    } disabled:opacity-60`}
                            >
                                {historyActionLoadingId === actionModal.item?.notificationId ? (
                                    <span className="inline-flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Processing...
                                    </span>
                                ) : actionModal.action === "yes" ? (
                                    "Approve"
                                ) : (
                                    "Reject"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default NotificationHistory;